import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "../../../stores/authStore";
import { channelsService } from "../../../lib/api/channels";
import { useAlertStore } from "../../../stores/alertStore";
import { useConfirmDialog } from "../../../components/ConfirmDialog";
import { getWebSocketClient } from "../../../lib/websocket/boardWebSocket";
import { useUserChannelsStore } from "../../../stores/userChannelsStore";
import { useOAuth } from "../../../hooks/useOAuth";
import ChannelContextMenu from "./ChannelContextMenu";
import { ChannelDetailModal } from "./modals/ChannelDetailModal";
import { Channel } from "../../../lib/api/channels";

interface ConnectedChannelsViewProps {
    onAddChannel?: () => void;
}

const ConnectedChannelsView = ({ onAddChannel, onMenuClick }: ConnectedChannelsViewProps & { onMenuClick?: () => void }) => {
    const { t } = useTranslation("common");
    const { showAlert } = useAlertStore();
    const { showDialog } = useConfirmDialog();

    const queryClient = useQueryClient();
    const { channels, isLoading: isLoadingChannels, loadChannels, syncStatusMessages } = useUserChannelsStore();
    const [isDeleting, setIsDeleting] = useState<string | null>(null);
    const [retryingChannels, setRetryingChannels] = useState<
        Record<string, boolean>
    >({});
    const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

    const handleOAuthSuccess = () => {
        loadChannels(true);
        queryClient.invalidateQueries({ queryKey: ['board-payload'] });
        showAlert({
            message: t("success.channelConnected"),
            type: "success"
        });
        // Match the delay in AddChannelModal for consistent UX
        setTimeout(() => {
            closePopup();
        }, 1500);
    };

    const handleOAuthError = (err: any) => {
        showAlert({
            message: err.message || t("channels.failedToConnect"),
            type: "error"
        });
    };

    const { initiateOAuth, closePopup } = useOAuth({
        onSuccess: handleOAuthSuccess,
        onError: handleOAuthError
    });

    const handleReconnect = async (channel: any) => {
        if (!channel.channelType) return;

        try {
            if (channel.channelType === 'shopify') {
                if (!channel.shopDomain) {
                    // Fail back to modal if we don't have the domain
                    if (onAddChannel) onAddChannel();
                    return;
                }

                // For Shopify, we redirect directly to the app in Shopify Admin
                const config = (window as any).__DASHBAL_CONFIG__;
                const appStoreHandle = config?.shopifyAppStoreHandle || 'dashbal';
                const redirectUrl = `https://${channel.shopDomain}/admin/apps/${appStoreHandle}`;
                
                window.open(redirectUrl, '_blank');
                showAlert({ message: t("channels.completeAuthorization"), type: "info" });
            } else if (channel.channelType === 'woocommerce') {
                if (!channel.shopDomain) {
                    if (onAddChannel) onAddChannel();
                    return;
                }
                
                // Redirect to WooCommerce connection page
                const connectUrl = `/connect/woocommerce?store_url=${encodeURIComponent(channel.shopDomain)}`;
                window.open(connectUrl, '_blank');
                showAlert({ message: t("channels.completeAuthorization"), type: "info" });
            } else {
                // For direct OAuth channels (Meta, TikTok)
                setRetryingChannels(prev => ({ ...prev, [channel.id]: true }));
                const response = await channelsService.initiateOAuth(channel.id, {
                    reconnectChannelId: channel.id
                });

                if (response.success && response.data.authUrl) {
                    initiateOAuth(response.data.authUrl);
                } else {
                    throw new Error("Failed to initiate OAuth flow");
                }
            }
        } catch (error: any) {
            console.error("Reconnection failed:", error);
            showAlert({ message: error.message || t("channels.failedToConnect"), type: "error" });
        } finally {
            setRetryingChannels(prev => ({ ...prev, [channel.id]: false }));
        }
    };

    // Load channels on mount
    useEffect(() => {
        loadChannels();
    }, []);

    // Set up WebSocket listener for channel sync updates
    useEffect(() => {
        const token = useAuthStore.getState().tokens?.accessToken;
        if (!token) return;

        let wsClient: ReturnType<typeof getWebSocketClient> | null = null;

        const getFreshToken = async (): Promise<string> => {
            try {
                const refreshTokens = useAuthStore.getState().refreshTokens;
                const newTokens = await refreshTokens();
                return newTokens.accessToken;
            } catch (error) {
                console.error('[ConnectedChannelsView] Failed to refresh token:', error);
                throw error;
            }
        };

        try {
            wsClient = getWebSocketClient(token, getFreshToken);

            // Connect to WebSocket
            wsClient.connect().catch(error => {
                console.error('[ConnectedChannelsView] WebSocket connection failed:', error);
            });

            // Handle channel synced event
            const handleChannelSynced = (data: any) => {
                console.log('[ConnectedChannelsView] Channel synced:', data.channelId);
                const store = useUserChannelsStore.getState();
                
                // Immediately update status to active to stop loading indicator
                store.updateChannel(data.channelId, {
                    status: (data.status && data.status !== 'syncing') ? data.status : 'active',
                    lastSyncAt: data.lastSyncAt || new Date().toISOString()
                });
                store.setSyncStatusMessage(data.channelId, "");

                // Delay the reload slightly to ensure backend DB has committed the change
                setTimeout(() => {
                    loadChannels(true);
                    // Also invalidate board queries
                    queryClient.invalidateQueries({ queryKey: ['board-payload'] });
                }, 500);
            };

            // Handle channel sync status event (transaction/populating phase only).
            // Only update the sync message — don't set status to 'syncing' because that
            // makes channel items flash a loader during data fetching. The loader should
            // only appear during the initial skeleton sync.
            const handleChannelSyncStatus = (data: any) => {
                if (data.channelId && data.message) {
                    const store = useUserChannelsStore.getState();
                    store.setSyncStatusMessage(data.channelId, data.message);
                }
            };

            // Handle channel sync failed event
            const handleChannelSyncFailed = (data: any) => {
                console.log('[ConnectedChannelsView] Channel sync failed:', data.channelId);
                const store = useUserChannelsStore.getState();
                store.updateChannel(data.channelId, {
                    status: data.status || 'error'
                });
                store.setSyncStatusMessage(data.channelId, "");

                setTimeout(() => {
                    loadChannels(true);
                    queryClient.invalidateQueries({ queryKey: ['board-payload'] });
                }, 500);
            };

            // Handle channel connected event
            const handleChannelConnected = (data: any) => {
                console.log('[ConnectedChannelsView] Channel connected:', data.channelId);
                const store = useUserChannelsStore.getState();
                if (data.channel) {
                    store.addChannel(data.channel);
                }
                loadChannels(true);
                queryClient.invalidateQueries({ queryKey: ['board-payload'] });
            };

            // Handle channel disconnected event
            const handleChannelDisconnected = (data: any) => {
                console.log('[ConnectedChannelsView] Channel disconnected:', data.channelId);
                loadChannels(true);
                queryClient.invalidateQueries({ queryKey: ['board-payload'] });
            };

            // Register event handler
            wsClient.on('channel:synced', handleChannelSynced);
            wsClient.on('channel:sync_status', handleChannelSyncStatus);
            wsClient.on('channel:sync_failed', handleChannelSyncFailed);
            wsClient.on('channel:connected', handleChannelConnected);
            wsClient.on('channel:disconnected', handleChannelDisconnected);

            // Cleanup
            return () => {
                if (wsClient) {
                    wsClient.off('channel:synced', handleChannelSynced);
                    wsClient.off('channel:sync_status', handleChannelSyncStatus);
                    wsClient.off('channel:sync_failed', handleChannelSyncFailed);
                    wsClient.off('channel:connected', handleChannelConnected);
                    wsClient.off('channel:disconnected', handleChannelDisconnected);
                }
            };
        } catch (error) {
            console.error('[ConnectedChannelsView] Failed to initialize WebSocket:', error);
        }
    }, [queryClient]);



    const handleRetrySync = async (channelId: string) => {
        setRetryingChannels((prev) => ({ ...prev, [channelId]: true }));
        try {
            const response = await channelsService.retryEntitySync(channelId);
            if (response.success) {
                // Reload channels to update status
                loadChannels(true);
                queryClient.invalidateQueries({ queryKey: ['board-payload'] });
            }
        } catch (error: any) {
            showAlert({
                message: String(t("userErrors.channels.unableToSync", "Unable to sync channel. Please try again.")),
                type: "error",
            });
        } finally {
            setRetryingChannels((prev) => ({ ...prev, [channelId]: false }));
        }
    };

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case "active":
                return "bg-success-100 text-success-700 border-success-200";
            case "syncing":
                return "bg-warning-100 text-warning-700 border-warning-200";
            case "error":
                return "bg-error-100 text-error-700 border-error-200";
            case "disconnected":
            case "uninstalled":
                return "bg-orange-100 text-orange-700 border-orange-200";
            case "inactive":
                return "bg-neutral-100 text-neutral-600 border-neutral-200";
            default:
                return "bg-neutral-100 text-neutral-600 border-neutral-200";
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status.toLowerCase()) {
            case "active":
                return (
                    <span className="w-3 h-3" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_2ef72bf2.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_2ef72bf2.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                );
            case "syncing":
                return (
                    <span className="w-3 h-3 animate-spin text-current" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_be8c64a0.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_be8c64a0.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                );
            case "error":
                return (
                    <span className="w-3 h-3" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_9f770c67.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_9f770c67.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                );
            case "disconnected":
            case "uninstalled":
                return (
                    <span className="w-3 h-3" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_a32ff851.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_a32ff851.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                );
            default:
                return (
                    <span className="w-3 h-3" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_17240952.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_17240952.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                );
        }
    };

    const handleDeleteChannel = async (
        channelId: string,
        channelName: string
    ) => {
        showDialog({
            title: String(t("channels.deleteChannel")),
            message: `Are you sure you want to delete ${channelName}?`,
            confirmText: String(t("actions.delete")),
            onConfirm: () => performDeleteChannel(channelId),
        });
    };

    const performDeleteChannel = async (channelId: string) => {
        setIsDeleting(channelId);
        try {
            const response = await channelsService.deleteChannel(channelId);

            if (response.success) {
                // Remove from the global store
                useUserChannelsStore.getState().removeChannel(channelId);
                // Also invalidate board queries
                queryClient.invalidateQueries({ queryKey: ['board-payload'] });
            } else {
                showAlert({ message: String(t("userErrors.channels.unableToDelete", "Unable to delete channel. Please try again.")), type: "error" });
            }
        } catch (error: any) {
            showAlert({
                message: String(t("userErrors.channels.unableToDelete", "Unable to delete channel. Please try again.")),
                type: "error",
            });
        } finally {
            setIsDeleting(null);
        }
    };

    const handleReloadChannel = async (channelId: string) => {
        try {
            const response = await channelsService.reloadChannel(channelId);
            if (response.success) {
                // Optimistically mark the channel as syncing so the list item
                // shows the same loader as a freshly added channel. The store's
                // merge guard would otherwise keep the prior 'active' status.
                useUserChannelsStore.getState().updateChannel(channelId, {
                    status: 'syncing',
                    lastReloadedAt: new Date().toISOString(),
                });
                showAlert({ message: t("channels.reloadInitiated"), type: "info" });
                loadChannels(true);
            } else {
                showAlert({ message: response.error || t("channels.reloadFailed"), type: "error" });
            }
        } catch (error: any) {
            showAlert({ message: t("channels.reloadFailed"), type: "error" });
        }
    };

    const handleChannelClick = (channel: Channel) => {
        setSelectedChannel(channel);
        setIsDetailModalOpen(true);
    };



    return (
        <>
            {/* Unified Header Structure */}
            <div className="flex items-center justify-between mb-4 pt-4 lg:pt-0 lg:mb-6 shrink-0">
                <div className="flex items-center gap-3">
                    {onMenuClick && (
                        <button
                            onClick={onMenuClick}
                            className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500 dark:text-neutral-400"
                        >
                            <span className="w-6 h-6" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_df5a8182.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_df5a8182.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                        </button>
                    )}
                    <div className="flex flex-col">
                        <h2 className="text-xl font-bold text-text-primary dark:text-neutral-100 tracking-tight">
                            {t('navigation.connectedChannels', 'Connected Channels')}
                        </h2>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 hidden lg:block">
                            {t('channels.manageConnections', 'Manage your connected data sources')}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {/* Hide button on mobile when empty to avoid "two headers/buttons" confusion */}
                    <button
                        onClick={onAddChannel}
                        className={`h-8 px-4 text-xs font-bold bg-accent-primary text-white rounded-lg hover:bg-accent-primary/90 transition-all shadow-sm active:scale-95 flex items-center gap-2 ${channels.length === 0 ? 'hidden lg:flex' : 'flex'}`}
                    >
                        <span className="w-3.5 h-3.5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_channels.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_channels.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                        <span>{t('channels.addChannel', 'Add Channel')}</span>
                    </button>
                </div>
            </div>

            {isLoadingChannels && channels.length === 0 ? (
                <>
                    {/* Skeleton for Add Channel button area */}
                    <div className="hidden lg:flex items-center justify-end mb-4">
                        <div className="h-9 w-[120px] bg-neutral-100 dark:bg-neutral-800 rounded-lg animate-pulse" />


                    </div>

                    <div className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm">


                        {/* Table headers */}
                        <div className="hidden md:flex items-center py-1.5 px-3 border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800/50">
                            <div className="flex-1 min-w-0">
                                <span className="text-[11px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                                    {t("boards.name")}
                                </span>
                            </div>
                            <div className="flex-shrink-0 w-32 px-3">
                                <span className="text-[11px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                                    {t("channels.status")}
                                </span>
                            </div>
                            <div className="flex-shrink-0 w-12"></div>
                        </div>

                        {/* Loading skeleton items */}
                        <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="flex items-center py-2 px-3 animate-pulse">
                                    <div className="flex-1 min-w-0 flex items-center gap-3">
                                        <div className="w-8 h-8 rounded bg-neutral-100 dark:bg-neutral-800 flex-shrink-0" />
                                        <div className="min-w-0">
                                            <div className="h-3 bg-neutral-100 dark:bg-neutral-800 rounded w-28 mb-1.5" />
                                            <div className="h-2.5 bg-neutral-100 dark:bg-neutral-800 rounded w-20" />
                                        </div>
                                    </div>
                                    <div className="hidden md:flex flex-shrink-0 w-32 px-3 items-center justify-end">
                                        <div className="h-[18px] w-14 bg-neutral-100 dark:bg-neutral-800 rounded-full" />
                                    </div>
                                    <div className="flex-shrink-0 w-12 flex items-center justify-end">
                                        <div className="w-6 h-6 bg-neutral-100 dark:bg-neutral-800 rounded" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            ) : channels.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center">
                    <div className="text-center">
                        <div className="w-12 h-12 bg-neutral-100 dark:bg-neutral-800 rounded-xl flex items-center justify-center mx-auto mb-4">


                            <span className="w-6 h-6 text-neutral-400" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_223e168d.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_223e168d.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                        </div>

                        <h3 className="text-sm font-semibold text-text-primary dark:text-neutral-200 mb-1">
                            {t("channels.noChannelsYet")}
                        </h3>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4 font-light max-w-xs mx-auto">
                            {t("channels.getStartedDescription")}
                        </p>
                        <button
                            id="onboarding-add-connection"
                            onClick={() => {
                                if (onAddChannel) onAddChannel();
                            }}
                            className="h-7 px-3 text-xs font-bold bg-accent-primary text-white rounded-lg hover:bg-accent-primary/90 transition-all shadow-sm active:scale-95"


                        >
                            {t("channels.addFirstChannel")}
                        </button>
                    </div>
                </div>
            ) : (
                <div className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm">


                    {/* Table headers */}
                    <div className="hidden md:flex items-center py-1.5 px-3 border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800/50">
                        <div className="flex-1 min-w-0">
                            <span className="text-[11px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                                {t("boards.name")}
                            </span>
                        </div>
                        <div className="flex-shrink-0 w-32 px-3">
                            <span className="text-[11px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                                {t("channels.status")}
                            </span>
                        </div>
                        <div className="flex-shrink-0 w-12"></div>
                    </div>

                    {/* Channel items */}
                    <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
                        {channels.map((channel) => (
                            <div
                                key={channel.id}
                                onClick={() => handleChannelClick(channel)}
                                className="group flex items-center py-2 px-3 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors cursor-pointer"
                            >
                                <div className="flex-1 min-w-0 flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center flex-shrink-0">


                                        {(() => {
                                            if (channel.icon && (channel.icon.startsWith("http") || channel.icon.startsWith("/"))) {
                                                const secureIcon = channel.icon.startsWith("http:")
                                                    ? channel.icon.replace("http:", "https:")
                                                    : channel.icon;
                                                return (
                                                    <img
                                                        src={secureIcon}
                                                        alt={channel.name}
                                                        className="w-5 h-5 object-contain"
                                                        onError={(e) => {
                                                            if (channel.channelTypeConfig?.icon && (channel.channelTypeConfig.icon.startsWith("/") || channel.channelTypeConfig.icon.startsWith("http"))) {
                                                                const fallbackIcon = channel.channelTypeConfig.icon.startsWith("http:")
                                                                    ? channel.channelTypeConfig.icon.replace("http:", "https:")
                                                                    : channel.channelTypeConfig.icon;
                                                                e.currentTarget.src = fallbackIcon;
                                                            } else {
                                                                e.currentTarget.style.display = 'none';
                                                            }
                                                        }}
                                                    />
                                                );
                                            }
                                            if (channel.channelTypeConfig?.icon && (channel.channelTypeConfig.icon.startsWith("/") || channel.channelTypeConfig.icon.startsWith("http"))) {
                                                const secureIcon = channel.channelTypeConfig.icon.startsWith("http:")
                                                    ? channel.channelTypeConfig.icon.replace("http:", "https:")
                                                    : channel.channelTypeConfig.icon;
                                                return <img src={secureIcon} alt={channel.name} className="w-5 h-5 object-contain" />;
                                            }
                                            if (channel.icon && !channel.icon.startsWith("http") && !channel.icon.startsWith("/")) {
                                                return <span className="text-sm">{channel.icon}</span>;
                                            }
                                            if (channel.channelTypeConfig?.icon) {
                                                return <span className="text-sm">{channel.channelTypeConfig.icon}</span>;
                                            }
                                            return <span className="text-sm">📊</span>;
                                        })()}
                                    </div>
                                    <div className="min-w-0">
                                        <h4 className="text-xs font-normal text-text-primary dark:text-neutral-200 truncate">
                                            {channel.name}
                                        </h4>
                                        <p className="text-[11px] text-neutral-500 dark:text-neutral-400 truncate">
                                            {channel.channelTypeConfig?.name || channel.name}
                                        </p>
                                        {channel.syncDetails?.commercialMessage && (channel.status === 'error' || channel.status === 'uninstalled') && (
                                            <p className="text-[10px] text-error-500 mt-0.5 leading-tight max-w-[200px]">
                                                {channel.syncDetails.commercialMessage}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="hidden md:flex flex-shrink-0 w-32 px-3 items-center justify-end">
                                    {channel.status === 'syncing' ? (
                                        <div className="flex flex-col items-end">
                                            <span className="text-[10px] font-medium text-accent-primary dark:text-accent-primary text-right leading-tight">
                                                {syncStatusMessages?.[String(channel.id)] ? (
                                                    (syncStatusMessages[String(channel.id)].startsWith('sync.') ? t(`${syncStatusMessages[String(channel.id)]}`) : syncStatusMessages[String(channel.id)])
                                                ) : t('channels.syncing')}
                                            </span>
                                        </div>
                                    ) : (
                                        <span
                                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] border ${getStatusColor(channel.status)}`}
                                            title={channel.syncDetails?.commercialAction || ""}
                                        >
                                            {getStatusIcon(channel.status)}
                                            {t(`channels.${channel.status.toLowerCase() === 'uninstalled' ? 'disconnected' : channel.status.toLowerCase()}`, channel.status)}
                                        </span>
                                    )}
                                </div>

                                <div className="flex-shrink-0 w-12 flex items-center justify-end gap-1">
                                    {(channel.status === "error" || channel.status === "disconnected" || channel.status === "uninstalled") && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (channel.status === "disconnected" || channel.status === "uninstalled") {
                                                    handleReconnect(channel);
                                                } else {
                                                    handleRetrySync(channel.id);
                                                }
                                            }}
                                            disabled={retryingChannels[channel.id]}
                                            className="p-1.5 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded text-neutral-500 dark:text-neutral-400 disabled:opacity-50"
                                            title={(channel.status === "disconnected" || channel.status === "uninstalled") ? (t("channels.reconnect") || "Reconnect") : (t("channels.retrySync") || "Retry Sync")}
                                        >
                                            {retryingChannels[channel.id] ? (
                                                <span className="animate-spin h-3.5 w-3.5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_1bd7cebe.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_1bd7cebe.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                            ) : (channel.status === "disconnected" || channel.status === "uninstalled") ? (
                                                <span className="w-3.5 h-3.5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_1e0a6f5d.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_1e0a6f5d.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                            ) : (
                                                <span className="w-3.5 h-3.5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_741f8e9e.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_741f8e9e.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                            )}
                                        </button>
                                    )}
                                    <ChannelContextMenu
                                        channel={channel}
                                        onDelete={handleDeleteChannel}
                                        isDeleting={isDeleting === channel.id}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <ChannelDetailModal
                isOpen={isDetailModalOpen}
                onClose={() => setIsDetailModalOpen(false)}
                channel={selectedChannel}
                onReload={handleReloadChannel}
                onDelete={handleDeleteChannel}
                onReconnect={handleReconnect}
                syncStatusMessage={selectedChannel ? syncStatusMessages?.[String(selectedChannel.id)] : undefined}
            />
        </>
    );
};

export default ConnectedChannelsView;
